import { OSINT } from "../interfaces"
import { QUERY_TYPE_BY_KEY } from "../constants"

const REPORT_HOSTS = /sherlock-report\.at|mysherlock\.app/

/**
 * Достать все упомянутые в результате телефоны:
 *   1) если payload.type === "phone" — payload.phone
 *   2) regex по rawMessages[].text:
 *      - «Телефон: ...», «Тел.: ...» (phone-запросы)
 *      - «Телефоны:\n - 79xxx» (VK/social-запросы, список через дефис)
 *   3) inlineButtons: t.me/+digits, wa.me/+digits
 * Дедупликация, нормализация (только цифры).
 */
export const extractPhones = (q: OSINT.QueryDto): string[] => {
  const set = new Set<string>()

  if (q.payload.type === "phone" && q.payload.phone) {
    set.add(q.payload.phone)
  }

  // Формат 1: «Телефон: 79xxx» или «Тел.: +7-900-xxx»
  const phoneInlineRe = /(?:Телефон|Тел\.?)\s*:?\s*\+?([\d\s\-()]{10,30})/g
  // Формат 2: «Телефоны:\n - 79xxx» — список номеров через дефис (каждый на своей строке)
  // Захватываем секцию целиком, затем вытаскиваем номера построчно.
  const phoneListSectionRe =
    /Телефоны:\s*\n((?:\s*-\s*[\d+][\d\s\-()]{8,}\n?)+)/g
  const phoneListItemRe = /^\s*-\s*\+?(\d{10,15})/gm

  for (const m of q.rawMessages ?? []) {
    const text = m.text ?? ""

    // Формат 1
    let match: RegExpExecArray | null
    phoneInlineRe.lastIndex = 0
    while ((match = phoneInlineRe.exec(text)) !== null) {
      const digits = match[1].replace(/\D/g, "")
      if (digits.length >= 10 && digits.length <= 15) set.add(digits)
    }

    // Формат 2: найти секцию «Телефоны:» и извлечь все «- 79xxx»
    phoneListSectionRe.lastIndex = 0
    let sectionMatch: RegExpExecArray | null
    while ((sectionMatch = phoneListSectionRe.exec(text)) !== null) {
      const section = sectionMatch[1]
      phoneListItemRe.lastIndex = 0
      let itemMatch: RegExpExecArray | null
      while ((itemMatch = phoneListItemRe.exec(section)) !== null) {
        const digits = itemMatch[1].replace(/\D/g, "")
        if (digits.length >= 10 && digits.length <= 15) set.add(digits)
      }
    }

    for (const btn of m.inlineButtons ?? []) {
      addPhoneFromUrl(btn.url, set)
    }
  }

  for (const btn of q.inlineButtons ?? []) {
    addPhoneFromUrl(btn.url, set)
  }

  return [...set]
}

const addPhoneFromUrl = (url: string | undefined, set: Set<string>) => {
  if (!url) return
  const tg = url.match(/t\.me\/\+(\d+)/)
  const wa = url.match(/wa\.me\/\+(\d+)/)
  if (tg) set.add(tg[1])
  if (wa) set.add(wa[1])
}

/**
 * Достать ссылку на полный отчёт.
 * Сначала ищем кнопку с текстом, начинающимся на «📄 Открыть полный отчет».
 * Если не нашли — берём первую login_url-кнопку с хостом sherlock-report.at | mysherlock.app.
 */
export const extractReportUrl = (q: OSINT.QueryDto): string | null => {
  const buttons = q.inlineButtons ?? []
  const byText = buttons.find((b) =>
    b.text?.startsWith("📄 Открыть полный отчет"),
  )
  if (byText?.url) return byText.url
  const byHost = buttons.find(
    (b) => b.kind === "login_url" && b.url && REPORT_HOSTS.test(b.url),
  )
  return byHost?.url ?? null
}

/**
 * Человекочитаемое представление payload для колонки «Вход» в таблице.
 * Используется и в CSV-экспорте.
 */
export const extractInputDisplay = (payload: OSINT.Payload): string => {
  const meta = QUERY_TYPE_BY_KEY[payload.type]
  return meta?.formatInput(payload) ?? ""
}
